import { useEffect, useState } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from "@mui/material";

interface MeetingTimeInputModalProps {
  open: boolean;
  workflowName: string;
  stepLabel: string;
  suggested?: { date?: string; startTime?: string };
  isSubmitting: boolean;
  onCancel: () => void;
  onConfirm: (fields: { date: string; startTime: string }) => void;
}

/**
 * Shown when a workflow run pauses at a meeting-booking step with no
 * resolvable date/time — see WorkflowRunnerService.suggestMeetingTime. The
 * suggestion (when present) pre-fills the fields but is never auto-applied;
 * the advisor always confirms or adjusts before the meeting actually books.
 */
export default function MeetingTimeInputModal({
  open,
  workflowName,
  stepLabel,
  suggested,
  isSubmitting,
  onCancel,
  onConfirm,
}: MeetingTimeInputModalProps) {
  const [date, setDate] = useState(suggested?.date ?? "");
  const [startTime, setStartTime] = useState(suggested?.startTime ?? "");

  useEffect(() => {
    if (open) {
      setDate(suggested?.date ?? "");
      setStartTime(suggested?.startTime ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const canConfirm = !!date && !!startTime;

  return (
    <Dialog open={open} onClose={isSubmitting ? undefined : onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>Meeting time needed</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          <strong>{workflowName}</strong> is paused at <strong>{stepLabel}</strong> — it needs a date and time to
          book this meeting.
        </Typography>
        {suggested?.date && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            Suggested from a related meeting — adjust if it's not right.
          </Typography>
        )}
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <TextField
            label="Date"
            type="date"
            size="small"
            fullWidth
            value={date}
            onChange={(e) => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Start time"
            type="time"
            size="small"
            fullWidth
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={isSubmitting}>
          Later
        </Button>
        <Button
          variant="contained"
          disabled={!canConfirm || isSubmitting}
          onClick={() => onConfirm({ date, startTime })}
        >
          {isSubmitting ? "Booking…" : "Confirm & book"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
